/**
 * @fileoverview Arc-specific student assessment drawer body. Mirrors the legacy
 * `StudentModalContent` but layered with the Arc design tweaks called out in
 * AR-535 (lesson Activities section, "Self-assessment" title, light file
 * buttons, curriculum item level 2/3 + selected-rubric viewer, colored item
 * border). The legacy component stays untouched to protect the
 * SessionMeetingAssessment page.
 */

import { useEffect, useRef, useState } from 'react'
import HeaderInfo from 'components/HeaderInfo/HeaderInfo'
import { isEmpty } from 'lodash'
import {
  WrapperModal,
  ContentContainer,
  LoaderContainer,
} from '@legacy/pages/SessionMeetingAssessment/components/StudentModalContent/StudentModalContentStyled'
import { useArcTranslation } from 'hooks'
import { api } from 'helpers'
import {
  IStudentAsssessments,
} from '@legacy/pages/SessionMeetingAssessment/components/StudentModalContent/StudentModalContentTypes'
import TextAreaLumiar from 'components/TextAreaLumiar/TextAreaLumiar'
import service from '@legacy/pages/SessionMeetingAssessment/components/StudentsAssessments/assessmentService'
import { notifications } from 'context'
import ErrorService from '@legacy/common/Tools/ErrorService'
import { useAssessmentScoreListHook } from 'common/hooks/useAssessmentScoreListHook'
import {
  Group,
  Paper,
  Box,
  Loader,
  Stack,
  Title,
  Text,
} from 'components'
import { IconMoodCog, IconBooks, IconClipboardList } from '@tabler/icons-react'
import parse from 'html-react-parser'
import { ArcEvaluativeItem } from './ArcEvaluativeItem'
import type { LessonActivityDetail } from './types'

// Type-level alias: HeaderInfo ignores the extra `active` prop at runtime, and
// `loading` was never passed here (undefined keeps the same behavior)
const HeaderInfoLoose = HeaderInfo as (
  props: Omit<Parameters<typeof HeaderInfo>[0], 'loading'> & { active?: boolean; loading?: boolean },
) => JSX.Element

interface ArcStudentAssessmentDrawerContentProps extends IStudentAsssessments {
  titleOverride?: string
  lessonActivities?: LessonActivityDetail[]
}

/** Debounce for the student note autosave while they type. */
const JOURNAL_AUTOSAVE_DEBOUNCE_MS = 700

export const ArcStudentAssessmentDrawerContent = ({
  sessionMeetingState,
  hasChanges,
  mixPanelTracker,
  setHasChanges,
  setStudents,
  setSelectedStudent,
  handleCloseDrawer,
  loadingDrawer,
  selectedStudent,
  sessionMeetingId,
  titleOverride,
  lessonActivities,
}: ArcStudentAssessmentDrawerContentProps) => {
  if (!selectedStudent) return <></>

  const { fullName, id, imageUrl, assessmentId: assessment_id, studentJournal } = selectedStudent

  const { t } = useArcTranslation()
  const { scrollRef, contentAssessmentScores, skillAssessmentScores, loadingAssess } =
    useAssessmentScoreListHook(id, sessionMeetingId, 'assessment')
  const [closing, setClosing] = useState(false)
  // The note typed by the student. Seeded from THEIR journal — never from the
  // teacher's (master) journal, which used to leak into this textarea (AR-770).
  const [studentNote, setStudentNote] = useState<string>(studentJournal ?? '')

  // Autosave each student score as soon as it is picked, so nothing is lost if
  // the drawer is closed without pressing "Save and close" (AR-771). Chained per
  // score id so repeated clicks on the same skill never persist out of order.
  const autosaveChains = useRef<Record<string, Promise<unknown>>>({})

  const autosaveScore = (scoreId: string, studentScore: number) => {
    const previous = autosaveChains.current[scoreId] ?? Promise.resolve()
    autosaveChains.current[scoreId] = previous
      .then(() => service.postScore({ assessment_score: { student_score: studentScore } } as any, scoreId))
      // Once persisted there is nothing unsaved, so the close/refresh warning
      // no longer needs to fire.
      .then(() => {
        setHasChanges(false)
        // Same success toast the teacher drawers show on each saved score.
        if (studentScore > 0) {
          notifications.show({ message: t('arc_teacher_assessment_drawer.toast.saved'), color: 'green' })
        }
      })
      .catch(() => undefined)
  }

  // Autosave the student's note, debounced while they type.
  const journalSaveTimeout = useRef<ReturnType<typeof setTimeout>>()

  const autosaveJournal = (description: string) => {
    if (!assessment_id) return
    if (journalSaveTimeout.current) clearTimeout(journalSaveTimeout.current)
    journalSaveTimeout.current = setTimeout(() => {
      // Persist through the student-safe endpoint, which only touches the
      // assessment's journal_student and student scores — never the teacher's
      // master lesson report (AR-770).
      api.patch('/api/students/update_report_student', {
        assessment_id,
        assessment: {
          assessment_id,
          journal_description: description,
          assessment_scores: [],
        },
      })
        .then(() => setHasChanges(false))
        .catch(() => undefined)
    }, JOURNAL_AUTOSAVE_DEBOUNCE_MS)
  }

  const updateJournal = (description: string) => {
    setHasChanges(true)
    setStudentNote(description)
    setSelectedStudent((student: any) => ({ ...student, studentJournal: description }))
    autosaveJournal(description)
  }

  // Scores are autosaved, so closing (the header X — there is no footer
  // Close button, matching the final self-assessment drawer) just refreshes
  // the list and dismisses the drawer, with no unsaved-changes prompt.
  const handleClose = async () => {
    if (closing) return
    setClosing(true)
    try {
      const updatedStudents = await service.getStudents(sessionMeetingState.session_meeting.id)
      setStudents(updatedStudents)
    } catch (e) {
      ErrorService.notice(e)
    }
    setClosing(false)
    handleCloseDrawer(false, true)
    document.body.style.overflow = 'scroll'
  }

  const Loading = () => (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
      <Loader />
    </div>
  )

  useEffect(() => {
    if (hasChanges) {
      mixPanelTracker('Preencher anotações de avaliação')
    }
  }, [hasChanges])

  if (loadingDrawer) {
    return (
      <WrapperModal id={id}>
        <Box mb='lg'>
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        </Box>
      </WrapperModal>
    )
  }

  const renderItemCard = (item: any) => (
    <Paper
      p='md'
      radius='md'
      withBorder
      key={item.id}
      // Inline style: `sx` loses to `withBorder` and the accent never rendered (AR-919)
      style={item.color ? { borderLeft: `4px solid ${item.color}` } : undefined}
    >
      <Stack spacing='md'>
        <ArcEvaluativeItem
          setHasChanges={setHasChanges}
          item={item}
          mixPanelTracker={mixPanelTracker}
          setSelectedStudent={setSelectedStudent}
          onAutosave={autosaveScore}
        />
      </Stack>
    </Paper>
  )

  return (
    <WrapperModal id={id} ref={scrollRef}>
      <HeaderInfoLoose
        title={titleOverride ?? t('students_assessment_partial_title')}
        userName={fullName}
        userImage={imageUrl}
        closeFunction={handleClose}
        active={!closing}
      />

      {lessonActivities && lessonActivities.length > 0 && (
        <Stack spacing='md' mt='md' mb='md'>
          <Group spacing={4}>
            <IconClipboardList size='1.5rem' />
            <Title order={3}>{t('students_assessment_activities_section')}</Title>
          </Group>
          {lessonActivities.map(activity => (
            <Box key={activity.id}>
              <Title order={5} mb='xs'>{activity.title}</Title>
              {activity.planning && (
                <Text size='sm' sx={{ whiteSpace: 'pre-line' }}>
                  {parse(activity.planning)}
                </Text>
              )}
            </Box>
          ))}
        </Stack>
      )}

      <ContentContainer active={!closing}>
        {!isEmpty(contentAssessmentScores?.result) && (
          <Stack spacing='md' mt='md'>
            <Group spacing={4}>
              <IconBooks size='1.5rem' />
              <Title order={3}>{t('students_assessment_standart')}</Title>
            </Group>
            {contentAssessmentScores?.result.map(renderItemCard)}
          </Stack>
        )}

        {!isEmpty(skillAssessmentScores?.result) && (
          <Stack spacing='md' mt='lg'>
            <Group spacing={4}>
              <IconMoodCog size='1.5rem' />
              <Title order={3}>{t('students_assessment_skills')}</Title>
            </Group>
            {skillAssessmentScores?.result.map(renderItemCard)}
          </Stack>
        )}

        {skillAssessmentScores?.pagination.isLastPage && (
          <div>
            <Text size='sm' weight={500} mt='md' mb='xs'>
              {t('student_tasks.assessment_drawer.journal_label')}
            </Text>

            <TextAreaLumiar
              value={studentNote}
              placeholder={t('arc_students_assessment_placeholder')}
              onChange={updateJournal}
              key={assessment_id}
            />
          </div>
        )}

        {loadingAssess && <Loading />}
      </ContentContainer>
    </WrapperModal>
  )
}
